import { useRef, useState } from "react";
import { ImagePlus, Loader2, UploadCloud, X } from "lucide-react";
import { uploadImage } from "@/lib/upload";

const msg = (e) => (typeof e.response?.data?.detail === "string" ? e.response.data.detail : e.message || "Upload failed. Please try again.");

export function ImageUpload({ value, onChange, client, path, params, testid, label, wide = false }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const pick = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setBusy(true); setErr("");
    try { onChange(await uploadImage(client, f, path, params)); } catch (x) { setErr(msg(x)); } finally { setBusy(false); }
  };
  return (
    <div className="space-y-1" data-testid={`${testid}-upload`}>
      {label && <span className="text-xs font-semibold text-slate-600">{label}</span>}
      <div className="flex items-center gap-3">
        <div className={`relative shrink-0 overflow-hidden rounded-lg border border-slate-200 bg-slate-100 ${wide ? "h-14 w-28" : "h-14 w-14"}`}>
          {value ? <img src={value} alt="" className="h-full w-full object-cover" data-testid={`${testid}-preview`} /> : <ImagePlus className="m-auto mt-4 h-5 w-5 text-slate-300" />}
        </div>
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-upload-button`}
          className="flex h-9 items-center gap-1.5 rounded-full border px-3 text-xs font-bold disabled:opacity-60">
          {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <ImagePlus className="h-3.5 w-3.5" />}{busy ? "Uploading…" : value ? "Replace" : "Upload"}
        </button>
        {value && <button type="button" onClick={() => onChange(null)} data-testid={`${testid}-remove`} className="text-slate-400 hover:text-rose-600" aria-label="Remove image"><X className="h-4 w-4" /></button>}
        <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={pick} data-testid={`${testid}-file-input`} />
      </div>
      {err && <p className="text-xs font-medium text-rose-600" data-testid={`${testid}-upload-error`}>{err}</p>}
    </div>
  );
}

export function ImageDropZone({ value, onChange, client, path, params, testid, label, ratio = "aspect-[3/1]" }) {
  const ref = useRef(null);
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");
  const [over, setOver] = useState(false);
  const take = async (f) => {
    if (!f) return;
    setBusy(true); setErr("");
    try { onChange(await uploadImage(client, f, path, params)); } catch (x) { setErr(msg(x)); } finally { setBusy(false); }
  };
  return (
    <div className="min-w-0 space-y-1.5" data-testid={`${testid}-upload`}>
      {label && <span className="text-xs font-semibold text-slate-600">{label}</span>}
      <div onDragOver={(e) => { e.preventDefault(); setOver(true); }} onDragLeave={() => setOver(false)} onDrop={(e) => { e.preventDefault(); setOver(false); take(e.dataTransfer.files?.[0]); }}
        className={`relative w-full overflow-hidden rounded-2xl border-2 border-dashed transition-colors ${ratio} ${over ? "border-amber-500 bg-amber-50" : "border-slate-200 bg-slate-50"}`}>
        {value ? <img src={value} alt="" className="absolute inset-0 h-full w-full object-cover" data-testid={`${testid}-preview`} />
          : <button type="button" onClick={() => ref.current?.click()} className="absolute inset-0 flex flex-col items-center justify-center gap-1 text-slate-400" data-testid={`${testid}-dropzone`}>
              <UploadCloud className="h-6 w-6" /><span className="px-2 text-center text-xs font-semibold">Tap or drop an image</span></button>}
        {busy && <div className="absolute inset-0 grid place-items-center bg-white/70"><Loader2 className="h-6 w-6 animate-spin text-slate-600" /></div>}
      </div>
      <div className="flex flex-wrap gap-2">
        <button type="button" onClick={() => ref.current?.click()} disabled={busy} data-testid={`${testid}-upload-button`} className="flex h-11 items-center gap-1.5 rounded-full border px-4 text-sm font-bold disabled:opacity-60">
          <ImagePlus className="h-4 w-4" />{busy ? "Uploading…" : value ? "Replace" : "Upload"}</button>
        {value && <button type="button" onClick={() => onChange(null)} data-testid={`${testid}-remove`} className="flex h-11 items-center gap-1 rounded-full px-3 text-sm font-semibold text-slate-500 hover:text-rose-600"><X className="h-4 w-4" />Remove</button>}
      </div>
      <input ref={ref} type="file" accept="image/jpeg,image/png,image/webp" className="hidden" onChange={(e) => { const f = e.target.files?.[0]; e.target.value = ""; take(f); }} data-testid={`${testid}-file-input`} />
      {err && <p className="text-xs font-medium text-rose-600" data-testid={`${testid}-upload-error`}>{err}</p>}
    </div>
  );
}
